<template>
    <div
        :class="disabled ? 'conversion-disabled' : 'conversion-input'"
        class="tw-inline-flex tw-items-center"
        :style="`--percentage-select-input-value: '${percentageFlag ? '%' : toggleSymbol}'`"
    >
        <Tooltip
            :disabled="forcePercentage || tooltipDisabled"
            label="Click to toggle between percentage and currency"
            :position="tooltipPosition"
        >
            <Button
                :active="percentageFlag"
                :disabled="disabled || lock || forcePercentage"
                class="tw-w-[40px] !tw-px-0 !tw-py-1.5 tw-rounded-r-none tw-border-r-0"
                @click="toggleType"
            >
                <span v-html="percentageFlag ? '%' : toggleSymbol" />
            </Button>
        </Tooltip>
        <NumberInput
            v-model="inputValue"
            class="tw-flex-1"
            :disabled="disabled"
            :loading="loading"
            :min="min"
            :step="percentageFlag ? 0.1 : 0.01"
            placeholder="0.00"
            :dp="2"
            :input-class="['tw-rounded-l-none tw-w-0 tw-min-w-[110px]', inputClass]"
        />
    </div>
</template>
<script lang="ts" setup>
import { useDataStore } from '@/js/stores/DataStore.js';
import Button from '@/js/components/controls/Button.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import NumberInput from '@/js/components/controls/NumberInput.vue';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { currencyToSymbol } from '@/js/utils.js';

const props = defineProps({
    baseValue: {
        type: [String, Number],
        default: null,
    },
    step: {
        type: Number,
        default: 0.01,
    },
    currency: {
        type: Boolean,
        default: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    percentage: {
        type: Boolean,
        default: false,
    },
    min: {
        type: Number,
        required: false,
    },
    lock: {
        type: Boolean,
        default: false,
    },
    forcePercentage: {
        type: Boolean,
        default: false,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    defaultInputValue: {
        type: [Number, String],
        default: null,
    },
    inputClass: {
        type: String,
        required: false,
    },
    currencyCode: {
        type: String,
        required: false,
    },
    tooltipPosition: {
        type: String,
        default: 'top',
    },
    tooltipDisabled: {
        type: Boolean,
        default: false,
    },
});
const model = defineModel<number | string | null>({
    type: [Number, String, null],
    default: 0,
});
const emit = defineEmits<{ toggle: [isPercentage: boolean] }>();
const store = useDataStore();
const percentageFlag = ref<boolean>(props.forcePercentage || props.percentage);
const isInitializing = ref<boolean>(true);
const inputValue = ref<string | number>(0);
const initialValue = ref<string | number>(0);

const toggleSymbol = computed<string>(() => {
    if (props.currency) {
        if (props.currencyCode) {
            return currencyToSymbol(props.currencyCode);
        }
        // if cs returns false, return '£'
        const cs = store.get('current_event.currency_symbol');
        // if this is an account level situation, current_event won't exist, so check the account.
        if (cs === false) {
            const acctCurrencyCode = store.get('account.currency_code');
            if (acctCurrencyCode) {
                return currencyToSymbol(acctCurrencyCode);
            }
            return '£';
        }
        // if cs is null or undefined, return '£'
        return cs ?? '£';
    }
    return '#';
});
const decimalPlaces = computed(() => {
    return props.step?.toString().split('.')[1]?.length ?? 2;
});
watch(
    () => inputValue.value,
    () => {
        calculateOutputValue();
    }
);
watch(
    () => props.percentage,
    (newVal) => {
        if (newVal === percentageFlag.value) {
            return;
        }
        if (newVal && props.baseValue !== null) {
            inputValue.value = ((100 / Number(props.baseValue)) * Number(inputValue.value)).toFixed(
                decimalPlaces.value
            );
        } else if (!newVal && props.baseValue) {
            inputValue.value = (Number(props.baseValue) * (Number(inputValue.value) / 100)).toFixed(
                decimalPlaces.value
            );
        }
        percentageFlag.value = newVal;
        calculateOutputValue();
    }
);
watch(
    () => model.value,
    (newVal) => {
        if (newVal === null || newVal === undefined) {
            return;
        }

        const expectedOutput =
            percentageFlag.value && props.baseValue !== null
                ? (parseFloat(props.baseValue.toString()) * (parseFloat(inputValue.value.toString()) / 100)).toFixed(
                      decimalPlaces.value
                  )
                : parseFloat(inputValue.value.toString()).toFixed(decimalPlaces.value);

        if (String(newVal) !== String(expectedOutput)) {
            inputValue.value = parseFloat(newVal.toString()).toFixed(decimalPlaces.value);
        }

        calculateOutputValue();
    }
);
onMounted(() => {
    // Only calculate a percentage if there is a base value
    // Otherwise treat this component as a number input for percentage
    if (model.value) {
        let finalInputValue: string | number =
            Number(model.value) > 0
                ? parseFloat(model.value.toString()).toFixed(decimalPlaces.value)
                : inputValue.value;

        if ((props.forcePercentage || props.percentage) && props.baseValue !== null) {
            if (props.defaultInputValue) {
                finalInputValue = props.defaultInputValue;
            } else {
                const baseValue = typeof props.baseValue === 'string' ? parseFloat(props.baseValue) : props.baseValue;
                const numberInputValue =
                    typeof finalInputValue === 'string' ? parseFloat(finalInputValue) : finalInputValue;
                finalInputValue = ((100 / baseValue) * numberInputValue).toFixed(decimalPlaces.value);
            }
        }

        inputValue.value = finalInputValue;
        initialValue.value = finalInputValue;
    }
    nextTick(() => {
        isInitializing.value = false;
    });
});
const toggleType = () => {
    if (props.disabled || props.lock) {
        return;
    }
    percentageFlag.value = !percentageFlag.value;
    if (percentageFlag.value && props.baseValue !== null) {
        inputValue.value = ((100 / Number(props.baseValue)) * Number(inputValue.value)).toFixed(decimalPlaces.value);
    } else if (props.baseValue) {
        inputValue.value = (Number(props.baseValue) * (Number(inputValue.value) / 100)).toFixed(decimalPlaces.value);
    }
    calculateOutputValue();
    emit('toggle', percentageFlag.value);
};
const calculateOutputValue = () => {
    if (isInitializing.value) {
        return;
    }
    if (inputValue.value === '') {
        inputValue.value = 0;
    }
    if (percentageFlag.value && props.baseValue !== null) {
        model.value = (
            parseFloat(props.baseValue.toString()) *
            (parseFloat(inputValue.value.toString()) / 100)
        ).toFixed(decimalPlaces.value);
    } else {
        model.value = parseFloat(inputValue.value.toString()).toFixed(decimalPlaces.value);
    }
};
</script>
<style lang="scss" scoped>
.conversion-input {
    display: flex;

    & > input {
        margin-left: 2px;

        &.is-small {
            width: 75px !important;
            height: 26px;
        }

        &.is_form {
            width: 75px !important;
            height: 26px;
        }
    }
}
.conversion-disabled {
    display: flex;

    & > input {
        background: rgba(black, 0.25);
        color: rgba(black, 0.25);
        border: 1px solid rgba(black, 0.75);
        margin-left: 2px;

        &.is-small {
            width: 92px !important;
            height: 26px;
        }

        &:disabled {
            background: rgba(black, 0.1) !important;
            color: rgba(white, 0.9);
            border: 1px solid rgba(black, 0.1);
        }
    }
}

.conversion-input,
.conversion-disabled {
    & > .switch {
        margin-right: 0 !important;

        &.is-small > span.check {
            height: 26px !important;
        }

        &.is-medium > span.check {
            height: 50px !important;
        }

        & > span.check {
            &:before {
                content: var(--percentage-select-input-value) !important;
                color: black;
                height: 100% !important;
                align-content: center;
                text-align: center;
            }
        }
    }
}
</style>
